/* ===========================
   Fabric Love - Member Core
   - Global member verification layer
   - Omnisend form submit detection
   - 30 day persistence
   - Omnisend campaign session detection
   - Applies ALL active member discount codes globally
   - Uses window.FL_OFFERS as single source of truth
   - Supports force-applying specific offer codes from PDP
   - Dispatches UI refresh events
   =========================== */

(function () {
  const CONFIG = window.FL_MEMBER_CONFIG || {};
  const PERSIST_DAYS = Number(CONFIG.persistDays || 30);
  const EXCLUDED_POPUP_FORM_IDS = Array.isArray(CONFIG.excludedPopupFormIds) ? CONFIG.excludedPopupFormIds : [];
  const EXCLUDED_EMBEDDED_FORM_IDS = Array.isArray(CONFIG.excludedEmbeddedFormIds) ? CONFIG.excludedEmbeddedFormIds : [];
  const DEBUG = !!CONFIG.debug;

  const STORAGE_KEYS = {
    memberSession: 'fl_member_session',
    memberPersistTs: 'fl_member_persist_ts',
    memberLastSource: 'fl_member_last_source',
    lastVerifiedEventTs: 'fl_member_verified_event_ts'
  };

  function log() {
    if (!DEBUG) return;
    try {
      console.log.apply(console, ['[FL Member]'].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  function ssGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function ssSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) {}
  }

  function lsGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function lsSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) {}
  }

  function track(eventName, params) {
    params = params || {};
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...params });
    } catch (e) {}
  }

  function nowTs() {
    return Date.now();
  }

  function memberPersistValid() {
    const ts = parseInt(lsGet(STORAGE_KEYS.memberPersistTs), 10);
    if (!ts) return false;
    const ttl = PERSIST_DAYS * 24 * 60 * 60 * 1000;
    return (nowTs() - ts) < ttl;
  }

  function hasMemberSession() {
    return ssGet(STORAGE_KEYS.memberSession) === '1';
  }

  function isVerifiedMember() {
    return hasMemberSession() || memberPersistValid();
  }

  function getRenderedOfferNodes() {
    return Array.from(document.querySelectorAll('[data-fl-member-offer][data-discount-code]'));
  }

  function getRenderedOfferCodes() {
    return getRenderedOfferNodes()
      .map(function (node) {
        return String(node.getAttribute('data-discount-code') || '').trim().toUpperCase();
      })
      .filter(Boolean)
      .filter(function (value, index, arr) {
        return arr.indexOf(value) === index;
      });
  }

  function getAllActiveMemberCodes() {
    if (!Array.isArray(window.FL_OFFERS)) {
      log('window.FL_OFFERS is not available yet.');
      return [];
    }

    return window.FL_OFFERS
      .filter(function (offer) {
        return offer && offer.enabled && offer.code;
      })
      .map(function (offer) {
        return String(offer.code || '').trim().toUpperCase();
      })
      .filter(Boolean)
      .filter(function (value, index, arr) {
        return arr.indexOf(value) === index;
      });
  }

  async function applyDiscountCode(code) {
    const url = '/discount/' + encodeURIComponent(code) + '?redirect=/';
    await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store'
    });
  }

  function refreshCartUI() {
    try {
      document.dispatchEvent(new Event('cart:refresh'));
      document.dispatchEvent(new Event('cart:updated'));
      document.dispatchEvent(new Event('cart:change'));

      fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store'
      }).then(function () {
        document.dispatchEvent(new Event('cart:refresh'));
        document.dispatchEvent(new Event('cart:updated'));
        document.dispatchEvent(new Event('cart:change'));
      }).catch(function () {});
    } catch (e) {}
  }

  async function applyDiscountCodes(codes, options) {
    options = options || {};
    const force = !!options.force;

    const normalizedCodes = (Array.isArray(codes) ? codes : [])
      .map(function (code) {
        return String(code || '').trim().toUpperCase();
      })
      .filter(Boolean)
      .filter(function (value, index, arr) {
        return arr.indexOf(value) === index;
      });

    if (!normalizedCodes.length) {
      log('No discount codes supplied for applyDiscountCodes().');
      return;
    }

    const codesToApply = force
      ? normalizedCodes
      : normalizedCodes.filter(function (code) {
          return ssGet('fl_member_discount_applied_' + code) !== '1';
        });

    if (!codesToApply.length) {
      log('All requested discount codes already applied this session.');
      return;
    }

    log(force ? 'Force applying discount codes:' : 'Applying discount codes:', codesToApply);

    for (const code of codesToApply) {
      try {
        await applyDiscountCode(code);
        ssSet('fl_member_discount_applied_' + code, '1');
        await new Promise(function (resolve) { setTimeout(resolve, 250); });
      } catch (e) {
        console.error('[FL Member] Failed to apply discount code:', code, e);
      }
    }

    setTimeout(refreshCartUI, 500);
  }

  async function applyRelevantMemberDiscounts() {
    const codes = getAllActiveMemberCodes();

    if (!codes.length) {
      log('No active member discount codes found.');
      return;
    }

    await applyDiscountCodes(codes, { force: false });
  }

  async function forceApplyCodes(codes) {
    await applyDiscountCodes(codes, { force: true });
  }

  function broadcastMemberStateChanged(source) {
    const detail = {
      verified: true,
      source: source || 'unknown'
    };

    window.dispatchEvent(new CustomEvent('fl_member_state_changed', { detail: detail }));
  }

  let verifyInFlight = false;

  async function markMemberVerified(source, options) {
    options = options || {};
    if (verifyInFlight) return;

    verifyInFlight = true;

    try {
      const ts = String(nowTs());

      ssSet(STORAGE_KEYS.memberSession, '1');
      lsSet(STORAGE_KEYS.memberPersistTs, ts);
      lsSet(STORAGE_KEYS.memberLastSource, source || 'unknown');
      ssSet(STORAGE_KEYS.lastVerifiedEventTs, ts);

      log('Member verified from source:', source);

      if (!options.skipTrack) {
        track('fl_member_verified', {
          source: source || 'unknown',
          page_path: location.pathname
        });
      }

      await applyRelevantMemberDiscounts();
      broadcastMemberStateChanged(source || 'unknown');
    } finally {
      verifyInFlight = false;
    }
  }

  function isOmnisendCampaignVisit() {
    try {
      const params = new URLSearchParams(window.location.search);
      const sourceParam = (CONFIG.omnisendCampaignRules && CONFIG.omnisendCampaignRules.sourceParam) || 'utm_source';
      const sourceValue = (CONFIG.omnisendCampaignRules && CONFIG.omnisendCampaignRules.sourceValue) || 'omnisend';
      const campaignParam = (CONFIG.omnisendCampaignRules && CONFIG.omnisendCampaignRules.campaignParam) || 'utm_campaign';

      const source = (params.get(sourceParam) || '').toLowerCase();
      const campaign = (params.get(campaignParam) || '').toLowerCase();

      return source === String(sourceValue).toLowerCase() && !!campaign;
    } catch (e) {
      return false;
    }
  }

  function classifyOmnisendForm(form) {
    const formId = String(form.id || '').trim();
    const displayType = String(form.displayType || '').toLowerCase();

    const isPopup = displayType === 'popup' || displayType === 'flyout';
    const isEmbedded = displayType === 'embedded';

    if (isPopup && EXCLUDED_POPUP_FORM_IDS.includes(formId)) {
      return { countsAsMember: false, reason: 'excluded_popup' };
    }

    if (isEmbedded && EXCLUDED_EMBEDDED_FORM_IDS.includes(formId)) {
      return { countsAsMember: false, reason: 'excluded_embedded' };
    }

    return {
      countsAsMember: true,
      reason: isPopup ? 'popup' : (isEmbedded ? 'embedded' : 'other')
    };
  }

  let omnisendSubmitHandledAt = 0;

  function wireOmnisendListener() {
    window.addEventListener('omnisendForms', function (e) {
      const detail = e.detail || {};
      const form = detail.form || {};

      if (detail.type !== 'submit') return;

      const formId = String(form.id || '');
      const displayType = String(form.displayType || '');
      const formName = String(form.name || '');

      log('Omnisend submit detected:', {
        id: formId,
        name: formName,
        displayType: displayType
      });

      const classification = classifyOmnisendForm(form);

      if (!classification.countsAsMember) {
        log('Omnisend form excluded from membership:', classification.reason, formId);
        return;
      }

      const now = nowTs();
      if (now - omnisendSubmitHandledAt < 1500) {
        log('Duplicate Omnisend submit ignored.');
        return;
      }
      omnisendSubmitHandledAt = now;

      markMemberVerified('omnisend_' + classification.reason + '_submit');
    });
  }

  function wireMemberVerifiedEvent() {
    window.addEventListener('fl_member_verified', function (e) {
      const source = e && e.detail && e.detail.source ? e.detail.source : 'custom_event';
      markMemberVerified(source);
    });
  }

  async function bootstrapMemberState() {
    if (isOmnisendCampaignVisit()) {
      await markMemberVerified('omnisend_campaign_click', { skipTrack: true });
      return;
    }

    if (memberPersistValid() && !hasMemberSession()) {
      await markMemberVerified('persisted_member', { skipTrack: true });
      return;
    }

    if (isVerifiedMember()) {
      await applyRelevantMemberDiscounts();
      broadcastMemberStateChanged(lsGet(STORAGE_KEYS.memberLastSource) || 'existing_member');
    }
  }

  window.FLMember = {
    isVerifiedMember: isVerifiedMember,
    memberPersistValid: memberPersistValid,
    markMemberVerified: markMemberVerified,
    applyRelevantMemberDiscounts: applyRelevantMemberDiscounts,
    forceApplyCodes: forceApplyCodes,
    getRenderedOfferCodes: getRenderedOfferCodes,
    getAllActiveMemberCodes: getAllActiveMemberCodes
  };

  document.addEventListener('DOMContentLoaded', function () {
    wireOmnisendListener();
    wireMemberVerifiedEvent();
    bootstrapMemberState();
  });
})();